import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApi, RequestType } from '@kapi';
import { EnvironmentVariablesService } from '@kenv';
import { DailyChallengeModel } from '@kp/shared/components/daily-challenge/daily-challenge.model';
import { DataStoreService } from '@kservice';
import { DataStatus, JsonObject, Status, StatusMessage } from '@ktypes/models';
import { DateTimeUtil } from '@kutil';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DailyChallengeStatusApi extends BaseApi {
  constructor(
    private client: HttpClient,
    public dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService
  ) {
    super(client, dataStoreService, _environmentVariablesService);
  }

  public async getCurrentStatus() {
    const now = new Date();
    const isEvening = DateTimeUtil.isEvening(now);
    if (isEvening) {
      /// DateTimeUtil isEvening work on the range [6pm - 4am] but the
      /// API will return query results based on a "new day" being after
      /// midnight. Subtracts 4 hours to continue querying for the previous
      /// day until 4am.
      now.setHours(now.getHours() - 4);
      return this.getStatusOn(now);
    }

    return await this.getStatusOn(new Date());
  }

  public async getStatusOn(date: Date) {
    return await this.getStatusBetween(date, date);
  }

  public async getStatusSince(from: Date) {
    return await this.getStatusBetween(from, new Date());
  }

  public async getStatusBetween(from: Date, to: Date) {
    return await this._getDailyChallengeStatus(from, to);
  }

  private async _getDailyChallengeStatus(startDate: Date, endDate: Date): Promise<DataStatus<DailyChallengeModel[]>> {
    const body = {
      minDate: DateTimeUtil.formatDate(startDate),
      maxDate: DateTimeUtil.formatDate(endDate),
    };

    const url = this.buildUrl('/daily-challenge', true, body);

    const request$ = this.performRequest<JsonObject[]>(RequestType.GET, url, { includeToken: true }).pipe(
      map((response: HttpResponse<JsonObject[]>): DataStatus<DailyChallengeModel[]> => {
        if (response?.ok) {
          const records = response.body.map((value: JsonObject) => new DailyChallengeModel().deserialize(value));
          return new DataStatus<DailyChallengeModel[]>(Status.done, new StatusMessage(200, ''), records);
        }
        return new DataStatus<DailyChallengeModel[]>(
          Status.error,
          new StatusMessage('ERROR', 'There was an error retrieving your daily challenge status'),
          null
        );
      }),
      catchError((error): Observable<DataStatus<DailyChallengeModel[]>> => {
        console.warn('Failed getting daily challenge status: ', error);
        return of(
          new DataStatus<DailyChallengeModel[]>(
            Status.error,
            new StatusMessage('ERROR', 'There was a server error retrieving your daily challenge status'),
            null
          )
        );
      })
    );
    return firstValueFrom(request$).catch((error) => {
      console.warn('Error getting daily challenge status: ', error);
      return null;
    });
  }
}
